import { useCallback, useEffect, useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';
import type { GridRowId } from '@mui/x-data-grid';
import type { GridInitialState } from '@mui/x-data-grid';

import { BooleanFilter, invariant } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';
import type {
  GenericStatusEnum,
  Id,
  ThirdPartyTypeEnum,
} from '@e-socna/data-layer';

import type { SelectionRestriction } from '../../../../components/ThirdParties/types/selection-restriction.type';

import { Selection } from './Selection';
import { checkIfIsMultiple } from './utils/check-if-is-multiple.util';
import { checkIfIsUsage } from './utils/check-if-is-usage.util';
import { withPositionIndexes } from './utils/with-position-indexes.util';
import type {
  ListOfIdFieldValue,
  ListOfUsageMaybeEmptyFieldValue,
  UniqueIdMaybeEmptyFieldValue,
  UniqueOrListMaybeEmptyFieldValue,
  UniqueUsageMaybeEmptyFieldValue,
} from './types/field-value.type';
import type { CreationPrefill } from './types/creation-prefill.type';

interface Props {
  readonly creationPrefill?: CreationPrefill;
  readonly enrichValue?: (
    thirdParty: UniqueIdMaybeEmptyFieldValue,
    index: number,
  ) => UniqueUsageMaybeEmptyFieldValue;
  readonly excludedIds?: ListOfIdFieldValue;
  readonly fieldName: string;
  readonly getId?: (value: UniqueUsageMaybeEmptyFieldValue) => GridRowId;
  readonly id: string;
  readonly initialGridState?: GridInitialState;
  readonly isInternal?: boolean;
  readonly kind?: ThirdPartyTypeEnum;
  readonly onCreateSuccess?: () => void;
  readonly selectionRestriction?: SelectionRestriction;
  readonly setIsFieldUsed?: (isUsing: boolean) => void;
  readonly status?: GenericStatusEnum;
  readonly subsetIds?: ListOfIdFieldValue;
}

export const ThirdPartiesSelectionFormStep = ({
  creationPrefill,
  enrichValue,
  excludedIds,
  fieldName,
  getId,
  initialGridState,
  isInternal,
  kind,
  onCreateSuccess: handleCreateSuccess,
  selectionRestriction,
  setIsFieldUsed,
  status,
  subsetIds,
}: Readonly<Props>) => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();

  invariant(
    fieldName in values,
    `${fieldName} must be defined if ThirdPartiesSelectionFormStep component is rendered. ${JSON.stringify(values)}`,
  );

  const fieldValue: UniqueOrListMaybeEmptyFieldValue = getIn(
    values,
    fieldName,
  ) as UniqueOrListMaybeEmptyFieldValue;

  const isMultiple = checkIfIsMultiple(fieldValue);
  const isUsage = checkIfIsUsage(fieldValue);

  invariant(
    !isUsage || !!getId,
    'getId must be defined if fieldValue is an object.',
  );

  useEffect(() => {
    setIsFieldUsed?.(
      isMultiple ? !!fieldValue.filter(BooleanFilter).length : !!fieldValue,
    );
  }, [fieldValue, isMultiple, setIsFieldUsed]);

  const cleanFieldValueAsId: readonly GridRowId[] = useMemo(() => {
    if (!isUsage) {
      return isMultiple
        ? (fieldValue.filter(BooleanFilter) as readonly GridRowId[])
        : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          fieldValue
          ? [fieldValue]
          : [];
    }

    return isMultiple
      ? (fieldValue as ListOfUsageMaybeEmptyFieldValue)
          .map((item) => {
            if (checkIfIsUsage(item) && getId) {
              return getId(item);
            }

            if (!checkIfIsUsage(item)) {
              return item;
            }

            throw new Error('getId must be defined if fieldValue is an object');
          })
          .filter(BooleanFilter)
      : fieldValue && getId?.(fieldValue)
        ? [getId(fieldValue)]
        : [];
  }, [fieldValue, getId, isMultiple, isUsage]);

  const handleSelectionChange = useCallback(
    (newSelection: readonly GridRowId[]) => {
      if (!isMultiple) {
        const newId = newSelection.length
          ? (newSelection[newSelection.length - 1] as Id)
          : EMPTY_STRING;

        void setFieldValue(fieldName, enrichValue?.(newId, -1) ?? newId);

        return;
      }

      const currentIdsSet = new Set<GridRowId>(cleanFieldValueAsId);
      const newIdsSet = new Set<GridRowId>(newSelection);

      const kept = (fieldValue as readonly unknown[]).filter((item) => {
        const id =
          isUsage && getId
            ? getId(item as UniqueUsageMaybeEmptyFieldValue)
            : (item as GridRowId);

        return newIdsSet.has(id);
      });

      const idsToAdd = newSelection.filter((id) => !currentIdsSet.has(id));

      const added = idsToAdd.map(
        (id, i) => enrichValue?.(id as Id, kept.length + i) ?? id,
      );

      void setFieldValue(fieldName, withPositionIndexes([...kept, ...added]));
    },
    [
      cleanFieldValueAsId,
      enrichValue,
      fieldName,
      fieldValue,
      getId,
      isMultiple,
      isUsage,
      setFieldValue,
    ],
  );

  return (
    <Grid
      container
      sx={{ height: '100%', display: 'flex', flexGrow: 1, flexWrap: 'nowrap' }}
    >
      <Grid
        size={12}
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <Selection
          cleanFieldValueAsId={cleanFieldValueAsId}
          creationPrefill={creationPrefill}
          excludedIds={excludedIds}
          initialGridState={initialGridState}
          isInternal={isInternal}
          kind={kind}
          onCreateSuccess={handleCreateSuccess}
          onSelectionChange={handleSelectionChange}
          selectionRestriction={selectionRestriction}
          status={status}
          subsetIds={subsetIds}
        />
      </Grid>
    </Grid>
  );
};
